// Оформить выдачу под отчёт. Сценарий 47.
//
// Панель не закрывается сама после успеха: в ответе приходит новый остаток человека и, если
// он выше порога, предупреждение. Закрыть панель мгновенно значит спрятать и то и другое, а
// именно ради этих двух чисел финотдел сюда и заходил.
//
// Остаток берётся ИЗ ОТВЕТА сервера, а не складывается в браузере: сложенное на клиенте
// разойдётся с сервером на первом же сторно, и человек будет верить экрану, а не кассе.

import type { FormEvent } from 'react';
import { useState } from 'react';

import type { ApiError } from '@/shared/errors/api-error';
import { CASH_METHODS, ошибкаДаты, type CashMethod } from '@/shared/api/endpoints/accountable';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { METHOD_TITLES } from '../dictionaries';
import { usePerson, useRegisterIssue } from '../queries';

const ISSUE_PERMISSION = 'core.accountable.issue';

export function IssuePanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, ISSUE_PERMISSION);
  const лицо = usePerson(id);
  const юрлица = useLegalEntitiesQuery();
  const операция = useRegisterIssue();

  const [сумма, setСумма] = useState('');
  const [способ, setСпособ] = useState<CashMethod>('transfer');
  const [юрлицо, setЮрлицо] = useState(NO_VALUE);
  const [назначение, setНазначение] = useState('');
  // Пустая строка, а не сегодняшний день: пустое поле означает «сейчас», и это честнее
  // подставленной полуночи. Выдача, оформленная в три часа дня, полуночью не была.
  const [дата, setДата] = useState('');
  const [ошибкаСуммы, setОшибкаСуммы] = useState<string | undefined>(undefined);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [ключ, setКлюч] = useState(newIdempotencyKey);

  const итог = операция.data;
  // Отказ по дате живёт ПОД полем даты, а общей плашкой внизу не дублируется: одна и та же
  // фраза в двух местах читается как две разные ошибки, и человек ищет вторую.
  const ошибкаДатыПоля = ошибкаДаты(операция.error);

  function отправить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    // Гейтим ДЕЙСТВИЕ, а не элемент: выключенная кнопка не доводит нажатие до обработчика,
    // и человек не получает ничего — со стороны это неотличимо от сломанной программы.
    if (!можно) {
      setЗапрет('Выдавать деньги под отчёт может финансовый отдел. Попросите его оформить выдачу.');
      return;
    }
    if (операция.isPending) {
      setЗапрет('Выдача уже проводится, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }
    const разбор = parseRublesToKopecks(сумма);
    if (!разбор.ok) {
      setОшибкаСуммы(moneyErrorText(разбор.reason, 'сумма выдачи'));
      return;
    }
    if (юрлицо === NO_VALUE) {
      setЗапрет('Выберите юридическое лицо: от него зависит, в чьей кассе учтётся выдача.');
      return;
    }
    setОшибкаСуммы(undefined);
    setЗапрет(null);
    операция.mutate(
      {
        body: {
          accountable_person_id: id,
          legal_entity_id: юрлицо,
          amount: разбор.value,
          method: способ,
          purpose: назначение.trim() === '' ? null : назначение.trim(),
          issued_at: дата === '' ? null : дата,
        },
        idempotencyKey: ключ,
      },
      {
        onSuccess: () => {
          // Новый ключ после успеха: следующая выдача — другая операция, и повтор прежнего
          // ключа вернул бы прошлую запись вместо новой.
          setКлюч(newIdempotencyKey());
          setСумма('');
          setНазначение('');
          // Дата сбрасывается вместе с суммой намеренно. Панель после успеха не закрывается,
          // и оставленная в поле прошлая дата легла бы в СЛЕДУЮЩУЮ выдачу — уже настоящую,
          // сегодняшнюю. Исправить её потом нечем: правки денежной записи не существует.
          setДата('');
        },
      },
    );
  }

  return (
    <form className='flex flex-col gap-4' onSubmit={отправить}>
      <p className='text-paragraph-sm text-text-sub-600'>
        Кому: {лицо.data?.employee_name ?? 'загружаем…'}
        {лицо.data !== undefined && (
          <> · сейчас на нём {formatKopecks(лицо.data.balance.unconfirmed_balance)}</>
        )}
      </p>

      <TextField
        id='issue-amount'
        label='Сумма'
        required
        value={сумма}
        onChange={setСумма}
        placeholder='30 000'
        error={ошибкаСуммы}
        // Как хранится сумма — наша кухня, человеку она не помогает ввести число.
        hint='Рубли, копейки можно через запятую.'
      />

      <SelectField
        id='issue-method'
        label='Как выдали'
        required
        value={способ}
        placeholder='наличными или переводом'
        options={CASH_METHODS.map((код) => ({ value: код, label: METHOD_TITLES[код] }))}
        onChange={(value) => setСпособ(value as CashMethod)}
      />

      <DateField
        id='issue-date'
        label='Когда выдали'
        value={дата}
        onChange={setДата}
        error={ошибкаДатыПоля}
        hint='Пусто — сейчас. Дата нужна, когда заносите выдачу, которая уже случилась: от неё считается, сколько дней деньги висят на человеке.'
      />

      <SelectField
        id='issue-entity'
        label='Юридическое лицо'
        required
        value={юрлицо}
        placeholder='выберите юрлицо'
        pending={юрлица.isPending}
        failed={юрлица.isError}
        failedError={юрлица.error as ApiError | null}
        emptyNote='Юридических лиц в справочнике нет. Их заводит администратор в настройках — без юрлица операция не проведётся.'
        options={(юрлица.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.short_name ?? строка.name,
        }))}
        onChange={setЮрлицо}
      />

      <TextField
        id='issue-purpose'
        label='Назначение'
        value={назначение}
        onChange={setНазначение}
        placeholder='Хозяйственные нужды по объектам Сургута'
        hint='Не обязательно, но без него через месяц никто не вспомнит, за что эти деньги.'
      />

      {запрет !== null && (
        <Alert.Root variant='lighter' status='warning' size='small'>
          <Alert.Icon />
          {запрет}
        </Alert.Root>
      )}
      {операция.error !== null && ошибкаДатыПоля === undefined && (
        <ErrorAlert error={операция.error as never} />
      )}

      {итог !== undefined && (
        <Alert.Root variant='lighter' status='success' size='small'>
          <Alert.Icon />
          Выдано {formatKopecks(итог.amount)} {METHOD_TITLES[итог.method]}.{' '}
          {итог.balance_after === null ? (
            <>
              Остаток по {итог.employee_name} сервер не прислал — посмотрите его в карточке
              человека.
            </>
          ) : (
            <>
              На {итог.employee_name} стало {formatKopecks(итог.balance_after)}.
            </>
          )}
          {итог.warning !== null && <> {итог.warning}</>}
        </Alert.Root>
      )}

      <div className='flex gap-2'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {операция.isPending ? 'Проводим…' : 'Выдать'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          {итог !== undefined ? 'Готово' : 'Отмена'}
        </Button.Root>
      </div>
    </form>
  );
}
